import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { ScoreFilterCriteria } from "@/components/candidates/filters/ScoreFilter";
import {
  ASSESSMENT_ROUND_FILTER_ROUNDS,
  ASSESSMENT_ROUND_FILTER_VALUE,
} from "@/constants/candidateRoundFilters";

export interface EnhancedCandidateFilters {
  search: string;
  status: string;
  round: string;
  college: string;
  position: string;
  city: string;
  qualification: string;
  branch: string;
  emailSent: string;
  year: string;
  sortBy: string;
  sortOrder: "asc" | "desc";
  scoreFilters: ScoreFilterCriteria[];
}

type EnhancedCandidateFiltersInput = Omit<EnhancedCandidateFilters, "scoreFilters"> & {
  scoreFilters: Omit<ScoreFilterCriteria, "id">[];
};

const FILTER_PARAM_KEYS = {
  search: "search",
  status: "status",
  round: "round",
  college: "college",
  legacyCollege: "collegeId",
  position: "position",
  city: "city",
  qualification: "qualification",
  branch: "branch",
  emailSent: "emailSent",
  year: "year",
  sortBy: "sortBy",
  sortOrder: "sortOrder",
  scoreFilters: "scoreFilters",
} as const;

const DEFAULT_SORT_BY = "created_at";
const DEFAULT_SORT_ORDER: "asc" | "desc" = "desc";
const PAGE_PARAM_KEY = "page";

const normalizeRoundFilter = (round: string): string =>
  ASSESSMENT_ROUND_FILTER_ROUNDS.some((assessmentRound) => assessmentRound === round)
    ? ASSESSMENT_ROUND_FILTER_VALUE
    : round;

const toNumber = (value: unknown): number | undefined => {
  if (typeof value !== "number") {
    return undefined;
  }

  return Number.isFinite(value) ? value : undefined;
};

const parseScoreFilters = (rawValue: string | null): ScoreFilterCriteria[] => {
  if (!rawValue) {
    return [];
  }

  try {
    const parsed = JSON.parse(rawValue);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .map((item, index) => {
        if (!item || typeof item !== "object") {
          return null;
        }

        const round =
          typeof item.round === "string" && item.round.trim().length > 0
            ? item.round
            : null;
        const minScore = toNumber(item.minScore);
        const maxScore = toNumber(item.maxScore);

        if (!round || minScore === undefined) {
          return null;
        }

        return {
          id:
            typeof item.id === "string" && item.id.trim().length > 0
              ? item.id
              : `${round}-${minScore}-${maxScore ?? ""}-${index}`,
          round,
          minScore,
          ...(maxScore !== undefined ? { maxScore } : {}),
        } as ScoreFilterCriteria;
      })
      .filter((filter): filter is ScoreFilterCriteria => filter !== null);
  } catch {
    return [];
  }
};

export function useEnhancedCandidateFilters(initial?: Partial<EnhancedCandidateFilters>) {
  const currentYear = new Date().getFullYear().toString();
  const [searchParams, setSearchParams] = useSearchParams();

  const updateFilter = <K extends keyof EnhancedCandidateFilters>(
    key: K,
    value: EnhancedCandidateFilters[K],
    options?: {
      emptyValue?: string;
      queryParam?: string;
      clearParamKeys?: string[];
    },
  ) => {
    const queryParam = options?.queryParam ?? key;
    const emptyValue = options?.emptyValue ?? "all";
    const clearParamKeys = options?.clearParamKeys ?? [];

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      clearParamKeys.forEach((paramKey) => next.delete(paramKey));

      if (
        value === undefined ||
        value === null ||
        value === "" ||
        value === emptyValue
      ) {
        next.delete(queryParam);
      } else {
        next.set(queryParam, String(value));
      }

      next.delete(PAGE_PARAM_KEY);

      return next;
    });
  };

  const filters = useMemo<EnhancedCandidateFilters>(() => {
    const search = searchParams.get(FILTER_PARAM_KEYS.search) ?? initial?.search ?? "";
    const status = searchParams.get(FILTER_PARAM_KEYS.status) ?? initial?.status ?? "all";
    const round = normalizeRoundFilter(
      searchParams.get(FILTER_PARAM_KEYS.round) ?? initial?.round ?? "all",
    );
    const college =
      searchParams.get(FILTER_PARAM_KEYS.college) ??
      searchParams.get(FILTER_PARAM_KEYS.legacyCollege) ??
      initial?.college ??
      "all";
    const position =
      searchParams.get(FILTER_PARAM_KEYS.position) ?? initial?.position ?? "all";
    const city = searchParams.get(FILTER_PARAM_KEYS.city) ?? initial?.city ?? "all";
    const qualification =
      searchParams.get(FILTER_PARAM_KEYS.qualification) ??
      initial?.qualification ??
      "all";
    const branch =
      searchParams.get(FILTER_PARAM_KEYS.branch) ?? initial?.branch ?? "all";
    const emailSent =
      searchParams.get(FILTER_PARAM_KEYS.emailSent) ?? initial?.emailSent ?? "all";
    const year = searchParams.get(FILTER_PARAM_KEYS.year) ?? initial?.year ?? currentYear;
    const sortBy =
      searchParams.get(FILTER_PARAM_KEYS.sortBy) ?? initial?.sortBy ?? DEFAULT_SORT_BY;
    const rawSortOrder =
      searchParams.get(FILTER_PARAM_KEYS.sortOrder) ??
      initial?.sortOrder ??
      DEFAULT_SORT_ORDER;
    const sortOrder: "asc" | "desc" = rawSortOrder === "asc" ? "asc" : "desc";

    const scoreFilters = parseScoreFilters(
      searchParams.get(FILTER_PARAM_KEYS.scoreFilters),
    );

    return {
      search,
      status,
      round,
      college,
      position,
      city,
      qualification,
      branch,
      emailSent,
      year,
      sortBy,
      sortOrder,
      scoreFilters,
    };
  }, [
    searchParams,
    initial?.search,
    initial?.status,
    initial?.round,
    initial?.college,
    initial?.position,
    initial?.city,
    initial?.qualification,
    initial?.branch,
    initial?.emailSent,
    initial?.year,
    initial?.sortBy,
    initial?.sortOrder,
    currentYear,
  ]);

  const setScoreFilters = (scoreFilters: ScoreFilterCriteria[]) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (scoreFilters.length === 0) {
        next.delete(FILTER_PARAM_KEYS.scoreFilters);
      } else {
        next.set(
          FILTER_PARAM_KEYS.scoreFilters,
          JSON.stringify(
            scoreFilters.map((filter) => ({
              id: filter.id,
              round: filter.round,
              minScore: filter.minScore,
              ...(filter.maxScore !== undefined
                ? { maxScore: filter.maxScore }
                : {}),
            })),
          ),
        );
      }
      next.delete(PAGE_PARAM_KEY);
      return next;
    });
  };

  const addScoreFilter = (filter: Omit<ScoreFilterCriteria, "id">) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const existingFilters = parseScoreFilters(
        prev.get(FILTER_PARAM_KEYS.scoreFilters),
      );
      const newFilter: ScoreFilterCriteria = {
        ...filter,
        id: Date.now().toString(),
      };

      const updatedFilters = [...existingFilters, newFilter];
      next.set(
        FILTER_PARAM_KEYS.scoreFilters,
        JSON.stringify(updatedFilters),
      );

      next.delete(PAGE_PARAM_KEY);

      return next;
    });
  };

  const removeScoreFilter = (id: string) => {
    setSearchParams((prev) => {
      const existingFilters = parseScoreFilters(
        prev.get(FILTER_PARAM_KEYS.scoreFilters),
      );
      const updatedFilters = existingFilters.filter((f) => f.id !== id);

      const next = new URLSearchParams(prev);
      if (updatedFilters.length === 0) {
        next.delete(FILTER_PARAM_KEYS.scoreFilters);
      } else {
        next.set(
          FILTER_PARAM_KEYS.scoreFilters,
          JSON.stringify(updatedFilters),
        );
      }

      next.delete(PAGE_PARAM_KEY);

      return next;
    });
  };

  const clearScoreFilters = () => {
    setScoreFilters([]);
  };

  const clearFilters = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);

      Object.values(FILTER_PARAM_KEYS).forEach((paramKey) => {
        next.delete(paramKey);
      });

      next.delete(PAGE_PARAM_KEY);

      return next;
    });
  };

  const applyFilters = (nextFilters: EnhancedCandidateFiltersInput) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);

      Object.values(FILTER_PARAM_KEYS).forEach((paramKey) => {
        next.delete(paramKey);
      });

      if (nextFilters.search !== "") {
        next.set(FILTER_PARAM_KEYS.search, nextFilters.search);
      }

      if (nextFilters.status !== "all") {
        next.set(FILTER_PARAM_KEYS.status, nextFilters.status);
      }

      const round = normalizeRoundFilter(nextFilters.round);
      if (round !== "all") {
        next.set(FILTER_PARAM_KEYS.round, round);
      }

      if (nextFilters.college !== "all") {
        next.set(FILTER_PARAM_KEYS.college, nextFilters.college);
      }

      if (nextFilters.position !== "all") {
        next.set(FILTER_PARAM_KEYS.position, nextFilters.position);
      }

      if (nextFilters.city !== "all") {
        next.set(FILTER_PARAM_KEYS.city, nextFilters.city);
      }

      if (nextFilters.qualification !== "all") {
        next.set(FILTER_PARAM_KEYS.qualification, nextFilters.qualification);
      }

      if (nextFilters.branch !== "all") {
        next.set(FILTER_PARAM_KEYS.branch, nextFilters.branch);
      }

      if (nextFilters.emailSent !== "all") {
        next.set(FILTER_PARAM_KEYS.emailSent, nextFilters.emailSent);
      }

      if (nextFilters.year !== currentYear) {
        next.set(FILTER_PARAM_KEYS.year, nextFilters.year);
      }

      if (nextFilters.sortBy !== DEFAULT_SORT_BY) {
        next.set(FILTER_PARAM_KEYS.sortBy, nextFilters.sortBy);
      }

      if (nextFilters.sortOrder !== DEFAULT_SORT_ORDER) {
        next.set(FILTER_PARAM_KEYS.sortOrder, nextFilters.sortOrder);
      }

      if (nextFilters.scoreFilters.length > 0) {
        next.set(
          FILTER_PARAM_KEYS.scoreFilters,
          JSON.stringify(
            nextFilters.scoreFilters.map((filter, index) => ({
              id: `${filter.round}-${filter.minScore}-${filter.maxScore ?? ""}-${index}`,
              round: filter.round,
              minScore: filter.minScore,
              ...(filter.maxScore !== undefined ? { maxScore: filter.maxScore } : {}),
            })),
          ),
        );
      }

      next.delete(PAGE_PARAM_KEY);

      return next;
    });
  };

  const hasActiveFilters =
    filters.search !== "" ||
    filters.status !== "all" ||
    filters.round !== "all" ||
    filters.college !== "all" ||
    filters.position !== "all" ||
    filters.city !== "all" ||
    filters.qualification !== "all" ||
    filters.branch !== "all" ||
    filters.emailSent !== "all" ||
    filters.year !== currentYear ||
    filters.scoreFilters.length > 0;

  return {
    // Filter values
    searchTerm: filters.search,
    statusFilter: filters.status,
    roundFilter: filters.round,
    collegeFilter: filters.college,
    positionFilter: filters.position,
    cityFilter: filters.city,
    qualificationFilter: filters.qualification,
    branchFilter: filters.branch,
    emailSentFilter: filters.emailSent,
    yearFilter: filters.year,
    sortBy: filters.sortBy,
    sortOrder: filters.sortOrder,
    scoreFilters: filters.scoreFilters,

    // Filter setters
    setSearchTerm: (value: string) =>
      updateFilter("search", value, {
        queryParam: FILTER_PARAM_KEYS.search,
        emptyValue: "",
      }),
    setStatusFilter: (value: string) =>
      updateFilter("status", value, {
        queryParam: FILTER_PARAM_KEYS.status,
      }),
    setRoundFilter: (value: string) =>
      updateFilter("round", normalizeRoundFilter(value), {
        queryParam: FILTER_PARAM_KEYS.round,
      }),
    setCollegeFilter: (value: string) =>
      updateFilter("college", value, {
        queryParam: FILTER_PARAM_KEYS.college,
        clearParamKeys: [FILTER_PARAM_KEYS.legacyCollege],
      }),
    setPositionFilter: (value: string) =>
      updateFilter("position", value, {
        queryParam: FILTER_PARAM_KEYS.position,
      }),
    setCityFilter: (value: string) =>
      updateFilter("city", value, {
        queryParam: FILTER_PARAM_KEYS.city,
      }),
    setQualificationFilter: (value: string) =>
      updateFilter("qualification", value, {
        queryParam: FILTER_PARAM_KEYS.qualification,
      }),
    setBranchFilter: (value: string) =>
      updateFilter("branch", value, {
        queryParam: FILTER_PARAM_KEYS.branch,
      }),
    setEmailSentFilter: (value: string) =>
      updateFilter("emailSent", value, {
        queryParam: FILTER_PARAM_KEYS.emailSent,
      }),
    setYearFilter: (value: string) =>
      updateFilter("year", value, {
        queryParam: FILTER_PARAM_KEYS.year,
      }),
    setSortBy: (value: string) =>
      updateFilter("sortBy", value, {
        queryParam: FILTER_PARAM_KEYS.sortBy,
        emptyValue: DEFAULT_SORT_BY,
      }),
    setSortOrder: (value: "asc" | "desc") =>
      updateFilter("sortOrder", value, {
        queryParam: FILTER_PARAM_KEYS.sortOrder,
        emptyValue: DEFAULT_SORT_ORDER,
      }),

    // Score filter methods
    onAddScoreFilter: addScoreFilter,
    onRemoveScoreFilter: removeScoreFilter,
    onClearScoreFilters: clearScoreFilters,

    // Utility methods
    applyFilters,
    clearFilters,
    hasActiveFilters,
  };
}
